import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Settings2 } from 'lucide-react';
import { NFTable } from '../components/NFTable';
import { NFSubmitModal } from '../components/NFSubmitModal';
import { useNotasFiscais } from '../hooks';
import { authService } from '../../../shared/lib/auth';
import type { NotaFiscal } from '../../../shared/types';
import { openAuthenticatedFile } from '../../../shared/lib/files';
import { EmptyState } from '../../../shared/components/EmptyState';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';

/**
 * Notas fiscais do próprio colaborador. A NF nasce da liberação do
 * financeiro; aqui o colaborador só envia (ou reenvia, se rejeitada).
 */
const NotasFiscaisPage: React.FC = () => {
  const toast = useToast();
  const { can } = usePermissions();
  const [enviando, setEnviando] = useState<NotaFiscal | null>(null);

  const user = authService.getCurrentUser();
  const { data, isLoading } = useNotasFiscais(user ? { collaboratorId: user.id, limit: 100 } : undefined);
  const notas = data?.data ?? [];

  const handleDownloadPDF = async (nota: NotaFiscal) => {
    if (nota.pdfUrl) await openAuthenticatedFile(nota.pdfUrl);
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notas Fiscais</h1>
          <p className="mt-1 text-gray-600">
            Envie a NF quando o financeiro liberar a competência e acompanhe o status de cada uma.
          </p>
        </div>
        {can(FEATURES.notasFiscaisLiberacao) && (
          <Link
            to="/notas-fiscais/gestao"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Settings2 className="h-4 w-4" /> Gestão de notas fiscais
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="mt-4 text-sm text-gray-500">Carregando notas fiscais...</div>
      ) : notas.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white">
          <EmptyState
            icon={<Lock className="h-10 w-10" />}
            title="Nenhuma NF liberada"
            description="O envio de nota fiscal depende da liberação do financeiro. Quando a competência for liberada, ela aparece aqui com o botão para enviar."
          />
        </div>
      ) : (
        <NFTable notas={notas} onDownloadPDF={handleDownloadPDF} onEnviar={setEnviando} />
      )}

      {enviando && (
        <NFSubmitModal nota={enviando} onClose={() => setEnviando(null)} onSent={(message) => toast.success(message)} />
      )}
    </div>
  );
};

export default NotasFiscaisPage;
